import { Button, Dropdown, Popconfirm, Space, Table } from 'antd';
import { motion } from 'framer-motion';
import { debounce } from 'lodash';
import { Edit, EllipsisVertical, MessageSquareShare, Plus, Trash } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import Highlighter from 'react-highlight-words';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import ContactFilters from '../../components/ContactFilters';
import ContactModal from '../../components/ContactModal';
import { getParamFromUrl, pageVariants } from '../../constants';
import useAuth from '../../hooks/useAuth';
import { changeStatus, deleteContact, getContacts } from '../../redux/actions/contact';
import { setFilters } from '../../redux/slices/filtersSlice';
import { store } from '../../redux/store';
import { encodeId } from '../../utils/secureId';
import { getRoutes } from './../../constants/Routes';
import BulkContactAssignee from '../../components/BulkContactAssignee';

const ContactsScreen = () => {
    const {
        user: { role },
    } = useAuth();
    const navigate = useNavigate();
    const { contacts, contactsPagination: pagination } = useSelector((state) => state.contactsSlice);
    const { filters } = useSelector((state) => state.filtersSlice);
    const [open, setOpen] = useState(false);
    const [bulkOpen, setBulkOpen] = useState(false);
    const [data, setData] = useState();
    const [search, setSearch] = useState(filters?.search || '');
    const status = Number(getParamFromUrl('status') ?? 1);

    const renderHighlightedText = (text) => (
        <Highlighter
            highlightClassName="highlighted-text"
            searchWords={[search]}
            autoEscape
            textToHighlight={text ? text.toString() : ''}
        />
    );

    const columns = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            width: 200,
            render: renderHighlightedText,
        },
        {
            title: 'Phone',
            dataIndex: 'phone',
            key: 'phone',
            width: 200,
            render: renderHighlightedText,
        },
        {
            title: 'Netsuite Customer',
            dataIndex: 'customer',
            key: 'customer',
            width: 250,
            render: (_, record) =>
                record?.customer_code &&
                record?.customer_name && (
                    <span>
                        <strong>({record?.customer_code})</strong> {record?.customer_name}
                    </span>
                ),
        },
        {
            title: 'Assigned User',
            dataIndex: 'assigned_user_name',
            key: 'assigned_user_name',
            width: 200,
            hidden: role === 2,
        },
        {
            title: 'Actions',
            key: 'actions',
            align: 'end',
            width: 200,
            render: (_, record) => {
                const menuItems = [
                    {
                        key: 'archive',
                        label: record.status !== 2 ? 'Archive' : 'Unarchive',
                        onClick: (e) => {
                            e.domEvent.stopPropagation();
                            changeStatus({
                                id: record.id,
                                status: record.status !== 2 ? 2 : 1,
                            });
                        },
                    },
                    {
                        key: 'block',
                        label: record.status === 0 ? 'Unblock' : 'Block',
                        danger: true,
                        onClick: (e) => {
                            e.domEvent.stopPropagation();
                            changeStatus({
                                id: record.id,
                                status: record.status !== 0 ? 0 : 1,
                            });
                        },
                    },
                ];

                return (
                    <Space>
                        <MessageSquareShare
                            size={18}
                            color="#6B7280"
                            cursor="pointer"
                            onClick={() => {
                                const id = encodeId(record.id);
                                navigate(getRoutes().chat_detail.route(id));
                            }}
                        />
                        <Edit
                            size={18}
                            color="#6B7280"
                            cursor="pointer"
                            onClick={() => {
                                setOpen(true);
                                setData(record);
                            }}
                        />
                        <Popconfirm
                            title="Delete User"
                            description="Are you sure to delete this user?"
                            onConfirm={() => handleDeleteContact(record?.id)}
                        >
                            <Trash size={18} color="#6B7280" cursor="pointer" />
                        </Popconfirm>
                        <Dropdown menu={{ items: menuItems }} trigger={['click']}>
                            <EllipsisVertical color="#6B7280" cursor="pointer" />
                        </Dropdown>
                    </Space>
                );
            },
        },
    ];

    const fetchUsers = useCallback(
        debounce((filters, value) => {
            getContacts({ ...filters, search: value });
        }, 500),
        []
    );

    const handleSearch = (e) => {
        const value = e.target.value;
        setSearch(value);
        store.dispatch(setFilters({ search: value }));
        fetchUsers(filters, value);
    };

    const handlePaginationChange = async (page) => {
        await getContacts({ ...filters, status, search, page });
    };

    const handleCancel = () => {
        setData();
        setOpen(false);
    };

    const handleDeleteContact = (id) => {
        deleteContact({
            id,
        });
    };

    useEffect(() => {
        getContacts({
            status,
        });
    }, []);

    return (
        <motion.div
            className="Contacts"
            initial="hidden"
            animate="visible"
            exit="hidden"
            variants={pageVariants}
        >
            <div className="header-flex">
                <div className="page-title">Contacts</div>
                <Space>
                    {role === 0 && (
                        <Button variant="filled" color="cyan" size="large" onClick={() => setBulkOpen(true)}>
                            Assign Contacts
                        </Button>
                    )}
                    <Button
                        variant="solid"
                        color="primary"
                        size="large"
                        icon={<Plus />}
                        onClick={() => setOpen(true)}
                    >
                        Add Contact
                    </Button>
                </Space>
            </div>
            <div className="theme-bg shadow">
                <motion.div
                    className="filter-header d-flex-none p-6"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0, transition: { duration: 0.3 } }}
                >
                    <ContactFilters
                        onSearchChange={handleSearch}
                        search={search}
                        onFilterChange={(filters) => store.dispatch(setFilters(filters))}
                    />
                </motion.div>
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0, transition: { duration: 0.4 } }}
                >
                    <Table
                        columns={columns}
                        dataSource={contacts}
                        rowKey="id"
                        components={{
                            header: {
                                cell: (props) => <th {...props} className="table-header px-2" />,
                            },
                            body: {
                                cell: (props) => <td {...props} className="theme-bg px-2 py-1" />,
                            },
                        }}
                        pagination={{
                            current: pagination?.page,
                            pageSize: pagination?.limit,
                            total: pagination?.total,
                            showSizeChanger: false,
                            onChange: handlePaginationChange,
                            position: ['bottomCenter'],
                        }}
                        scroll={{ x: 'max-content', y: 'calc(100dvh - 360px)' }}
                    />
                </motion.div>
            </div>
            {open && <ContactModal open={open} onCancel={handleCancel} data={data} />}
            {bulkOpen && <BulkContactAssignee open={bulkOpen} onCancel={() => setBulkOpen(false)} />}
        </motion.div>
    );
};

export default ContactsScreen;
